import React, { useEffect, useMemo, useState } from 'react';
import { api, createCoordinatedDeployment, createProjectDeployment, DeploymentImpact, DeploymentScope, Environment, EnvironmentHealth, ProjectApplication, RepositoryAccess } from '../api';
import { DeployReadiness } from './deploy/DeployReadiness';
import { ChangesSinceLiveNote } from './deploy/ChangesSinceLiveNote';
import type { SectionPath } from './project/operationalHealth';
import { applicationTypeLabel, humanize } from '../labels';
import { ErrorNotice } from './ui';
import { Checkbox, Dialog, Field, Input, Segmented } from './primitives';
import { DeploymentImpactPanel } from './DeploymentImpactPanel';
import { useIdempotencyKey } from '../idempotency';

interface NewDeploymentModalProps {
  projectId: string;
  /** The environment being deployed; its placement decides the server. */
  environment: Environment;
  isHeadless?: boolean;
  applications?: ProjectApplication[];
  /** Components to preselect (CO-48); all deployable ones when omitted. */
  initialComponentIds?: string[];
  /** Components that cannot be deployed here (not placed in this environment). */
  unplacedComponentIds?: string[];
  onClose: () => void;
  onCreated: (deploymentId: string) => void;
  /** Called instead of onCreated when several components were deployed together. */
  onGroupCreated?: (groupId: string) => void;
  /** The environment's health as the server derived it (CO-95): readiness at the top. */
  health?: EnvironmentHealth | null;
  /** Which credential reads the repository (CO-98). */
  repositoryAccess?: RepositoryAccess | null;
  /** Links the readiness items and the repository-access status. */
  sectionPath?: SectionPath;
  /** Called when the person started a database change preview of the Magento commit. */
  onPreviewCreated?: (previewId: string) => void;
}

type CommitSource = 'branch' | 'commit';

interface ReleaseChoice {
  branch: string;
  source: CommitSource;
  commitSha: string;
  resolvedSha: string | null;
  resolving: boolean;
  resolveError: string | null;
}

/** An unconfigured component starts with an empty branch: the person names
 *  one rather than Commerce Ops deploying a branch nobody chose. */
function initialChoice(defaultBranch: string | undefined): ReleaseChoice {
  return { branch: defaultBranch ?? '', source: 'branch', commitSha: '', resolvedSha: null, resolving: false, resolveError: null };
}

/**
 * NewDeploymentModal starts a deployment of one environment. Commerce Ops
 * resolves the branch to its current commit through the project's Git
 * connection, so the normal path is "deploy the latest commit of the
 * component's branch"; pasting an exact commit is the exception.
 */
export const NewDeploymentModal: React.FC<NewDeploymentModalProps> = ({
  projectId,
  environment,
  isHeadless = false,
  applications = [],
  initialComponentIds,
  unplacedComponentIds = [],
  onClose,
  onCreated,
  onGroupCreated,
  health,
  repositoryAccess,
  sectionPath,
  onPreviewCreated,
}) => {
  const backendApp = applications.find((a) => a.type === 'magento_backend');

  // Components (CO-48): a headless project deploys any selection of its
  // components; one selected component is an ordinary single deployment,
  // several are a coordinated deployment in dependency order.
  const deployable = useMemo(
    () => applications.filter((a) => !a.disabled && !unplacedComponentIds.includes(a.id)),
    [applications, unplacedComponentIds],
  );
  // Opened for named components (a component's own Deploy), those are
  // selected. Opened without (the header's Deploy) on a project with
  // several components, nothing is: deploying every component, Magento
  // included, is a choice the person makes, never a default (UX-07).
  const [selected, setSelected] = useState<string[]>(() => {
    if (initialComponentIds && initialComponentIds.length > 0) return initialComponentIds.filter((id) => deployable.some((a) => a.id === id));
    return isHeadless && deployable.length > 1 ? [] : deployable.map((a) => a.id);
  });
  const selectedApps = deployable.filter((a) => selected.includes(a.id));
  const selectedFrontends = selectedApps.filter((a) => a.type === 'frontend');
  const frontendApp = selectedFrontends[0];
  const firstSelected = selectedApps[0];
  const coordinated = isHeadless && selectedApps.length > 1;
  const scope: DeploymentScope = !isHeadless
    ? 'magento_only'
    : coordinated
      ? (selectedApps.length === deployable.length ? 'all' : 'selected')
      : selectedApps[0]?.type === 'frontend'
        ? 'frontend_only'
        : 'magento_only';
  const [choices, setChoices] = useState<Record<string, ReleaseChoice>>(() =>
    Object.fromEntries(applications.map((a) => [a.id, initialChoice(a.default_branch)])),
  );
  const choiceFor = (id: string): ReleaseChoice => choices[id] ?? initialChoice('main');
  const setChoiceFor = (id: string): React.Dispatch<React.SetStateAction<ReleaseChoice>> => (update) =>
    setChoices((all) => ({ ...all, [id]: typeof update === 'function' ? update(all[id] ?? initialChoice('main')) : update }));
  const [impact, setImpact] = useState<DeploymentImpact | null>(null);
  const [rehearsal, setRehearsal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const idempotency = useIdempotencyKey();
  const deploysMagento = !isHeadless || selectedApps.some((a) => a.type === 'magento_backend');
  const deploysFrontend = isHeadless && selectedFrontends.length > 0;

  // The blast radius of what is about to be deployed: every website for a
  // backend release, one storefront for a frontend release. A coordinated
  // deployment shows the widest of its members.
  useEffect(() => {
    let cancelled = false;
    const impactScope: DeploymentScope = deploysMagento ? (deploysFrontend ? 'combined' : 'magento_only') : 'frontend_only';
    if (!deploysMagento && !deploysFrontend) {
      setImpact(null);
      return;
    }
    api.getDeploymentImpact(environment.id, impactScope, deploysFrontend && !deploysMagento ? frontendApp?.id : undefined)
      .then((r) => { if (!cancelled) setImpact(r.impact); })
      .catch(() => { if (!cancelled) setImpact(null); });
    return () => { cancelled = true; };
  }, [environment.id, deploysMagento, deploysFrontend, frontendApp?.id]);

  async function resolve(app: ProjectApplication | undefined, choice: ReleaseChoice, set: React.Dispatch<React.SetStateAction<ReleaseChoice>>) {
    if (!app) return;
    const branch = choice.branch.trim();
    if (!branch) return;
    set((c) => ({ ...c, resolving: true, resolveError: null }));
    try {
      const res = await api.resolveApplicationBranch(projectId, app.id, branch);
      set((c) => ({ ...c, resolving: false, resolvedSha: res.commit_sha }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Commerce Ops could not look up this branch.';
      set((c) => ({
        ...c,
        resolving: false,
        resolvedSha: null,
        resolveError: /[.!?]$/.test(message) ? message : `${message}.`,
      }));
    }
  }

  // Resolve the default branch of every selected component as soon as the
  // modal opens, and again whenever a branch name changes (debounced), so
  // the SHA is ready by the time someone reaches the Deploy button.
  const targets: ProjectApplication[] = isHeadless ? selectedApps : backendApp ? [backendApp] : [];
  const branchKey = targets.map((a) => `${a.id}:${choiceFor(a.id).branch}:${choiceFor(a.id).source}`).join('|');
  useEffect(() => {
    const timers = targets
      .filter((a) => choiceFor(a.id).source === 'branch' && !choiceFor(a.id).resolvedSha)
      .map((a) => setTimeout(() => resolve(a, choiceFor(a.id), setChoiceFor(a.id)), 400));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchKey]);

  function shaOf(choice: ReleaseChoice): string {
    return choice.source === 'branch' ? (choice.resolvedSha ?? '') : choice.commitSha.trim().toLowerCase();
  }

  const notReady = targets.find((a) => shaOf(choiceFor(a.id)).length < 7);
  const allReady = targets.length > 0 && !notReady;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (targets.length === 0) {
      setError(new Error('Choose at least one component to deploy.'));
      return;
    }
    if (notReady) {
      const c = choiceFor(notReady.id);
      setError(new Error(c.source === 'branch' ? `Wait for the ${notReady.name} branch to resolve, or enter an exact commit.` : `Enter at least the first 7 characters of the ${notReady.name} commit.`));
      return;
    }
    setLoading(true);
    try {
      if (coordinated) {
        const resp = await createCoordinatedDeployment(projectId, {
          environmentId: environment.id,
          mode: rehearsal ? 'rehearsal' : 'live',
          scope,
          start: true,
          components: selectedApps.map((a) => ({ applicationId: a.id, branch: choiceFor(a.id).branch.trim(), commitSha: shaOf(choiceFor(a.id)) })),
        }, idempotency.key());
        idempotency.settle();
        if (onGroupCreated) onGroupCreated(resp.group.id);
        else onCreated(resp.deployments[0]?.id ?? '');
        return;
      }
      const magentoChoice = backendApp ? choiceFor(backendApp.id) : initialChoice('main');
      const frontendChoice = frontendApp ? choiceFor(frontendApp.id) : initialChoice('main');
      const resp = await createProjectDeployment(projectId, {
        environmentId: environment.id,
        mode: rehearsal ? 'rehearsal' : 'live',
        scope,
        backendApplicationId: backendApp?.id,
        frontendApplicationId: deploysFrontend ? frontendApp?.id : undefined,
        branch: magentoChoice.branch.trim(),
        commitSha: deploysMagento ? shaOf(magentoChoice) : '',
        frontendBranch: frontendChoice.branch.trim(),
        frontendCommitSha: deploysFrontend ? shaOf(frontendChoice) : '',
        // Deploy means deploy, as it does for several components. A start the
        // server refuses leaves the deployment pending on its own page.
        start: true,
        options: {
          hasNextJs: isHeadless || deploysFrontend,
        },
      }, idempotency.key());
      idempotency.settle();
      onCreated(resp.deployment.id);
    } catch (err) {
      idempotency.settle(err);
      setError(err instanceof Error ? err : new Error(String(err)));
      setLoading(false);
    }
  };

  const isProduction = environment.kind === 'production';
  // A live deployment cannot work while the server knows a blocker (the
  // agent is gone, setup failed); a rehearsal touches nothing and stays.
  const blocked = !rehearsal && (health?.attention ?? []).some((item) => item.blocks_deploy);
  const targetWord = !isHeadless
    ? 'Magento'
    : selectedApps.length === 0
      ? 'nothing'
      : selectedApps.length === deployable.length && deployable.length > 1
        ? 'all components'
        : selectedApps.length === 1 && firstSelected
          ? firstSelected.name
          : `${selectedApps.length} components`;

  return (
    <Dialog
      open
      size="lg"
      title={`Deploy to ${environment.name}`}
      description={isProduction ? 'This is production.' : undefined}
      onClose={onClose}
      busy={loading}
      onSubmit={handleSubmit}
      footer={
        <>
          <button type="button" className="button button--secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button type="submit" className="button button--primary" disabled={loading || !allReady || blocked} data-testid="deploy-submit">
            {loading
              ? 'Starting'
              : rehearsal
                ? 'Rehearse deployment'
                : selectedApps.length === 0 && isHeadless
                  ? 'Choose what to deploy'
                  : `Deploy ${targetWord} to ${environment.name}`}
          </button>
        </>
      }
    >
      <DeployReadiness health={health} repositoryAccess={repositoryAccess} sectionPath={sectionPath} environmentName={environment.name} />

      <p className="dialog__text">The store only enters maintenance mode if the release changes the database.</p>

      <ErrorNotice error={error} />

      {isHeadless && (
        <fieldset className="deploy-pick__fieldset" data-testid="deploy-components">
          <legend className="deploy-pick__legend">What to deploy</legend>
          <ul className="deploy-pick" aria-label="Components">
            {applications.map((a) => {
              const canPick = deployable.some((d) => d.id === a.id);
              const why = a.disabled ? 'disabled' : unplacedComponentIds.includes(a.id) ? 'not placed in this environment' : '';
              return (
                <li key={a.id}>
                  <label className={`deploy-pick__item ${!canPick ? 'deploy-pick__item--off' : ''}`}>
                    <Input
                      type="checkbox"
                      checked={selected.includes(a.id)}
                      disabled={!canPick}
                      onChange={(e) => setSelected((s) => (e.target.checked ? [...s, a.id] : s.filter((id) => id !== a.id)))}
                      data-testid={`deploy-pick-${a.slug}`}
                    />
                    <span className="deploy-pick__name">{a.name}</span>
                    <span className="muted">{applicationTypeLabel[a.type] ?? humanize(a.type)}{why ? ` · ${why}` : ''}</span>
                  </label>
                </li>
              );
            })}
          </ul>
          <div className="deploy-pick__footer">
            <button type="button" className="button button--ghost button--small" onClick={() => setSelected(deployable.map((a) => a.id))} disabled={selected.length === deployable.length}>
              Select all
            </button>
            <span className="type-meta">
              {selectedApps.length === 0
                ? ''
                : coordinated
                ? `${selectedApps.length} components deploy in dependency order — Magento first, then its storefronts — each with its own release and rollback. A failure stops only what depends on it.`
                : 'One component deploys on its own; the others are not rebuilt or restarted.'}
            </span>
          </div>
        </fieldset>
      )}

      {targets.map((a) => (
        <ReleasePicker
          key={a.id}
          title={`${a.name} release`}
          application={a}
          choice={choiceFor(a.id)}
          onChange={setChoiceFor(a.id)}
          idPrefix={a.type === 'magento_backend' ? 'magento' : `frontend-${a.slug}`}
          changes={
            <>
              <ChangesSinceLiveNote projectId={projectId} applicationId={a.id} environmentId={environment.id} commit={shaOf(choiceFor(a.id)) || null} />
              {a.type === 'magento_backend' && onPreviewCreated ? (
                <PreviewDatabaseAction environmentId={environment.id} commit={shaOf(choiceFor(a.id))} branch={choiceFor(a.id).source === 'branch' ? choiceFor(a.id).branch.trim() : ''} onCreated={onPreviewCreated} />
              ) : null}
            </>
          }
        />
      ))}

      <DeploymentImpactPanel impact={impact} />

      <Checkbox
        checked={rehearsal}
        onChange={(e) => setRehearsal(e.target.checked)}
        label={
          <>
            <strong>Rehearse only.</strong> Records the steps without sending anything to the server; nothing is built,
            switched or activated.
          </>
        }
      />
    </Dialog>
  );
};


function ReleasePicker({
  title,
  application,
  choice,
  onChange,
  idPrefix,
  changes,
}: {
  title: string;
  application: ProjectApplication | undefined;
  choice: ReleaseChoice;
  onChange: React.Dispatch<React.SetStateAction<ReleaseChoice>>;
  idPrefix: string;
  /** What this release changes since the live one (CO-98). */
  changes?: React.ReactNode;
}) {
  return (
    <section className="release-picker" aria-labelledby={`${idPrefix}-release-title`}>
      <div className="release-picker__head">
        <h3 id={`${idPrefix}-release-title`} className="release-picker__title">
          {title}
        </h3>
        {application?.repository_url ? (
          <code className="release-picker__repo">{application.repository_url.replace(/^(https?:\/\/|git@)/, '').replace(/\.git$/, '')}</code>
        ) : null}
      </div>

      <div className="release-picker__row">
        <Field label="Branch">
          <Input
            id={`${idPrefix}-branch`}
            type="text"
            value={choice.branch}
            onChange={(e) => onChange((c) => ({ ...c, branch: e.target.value, resolvedSha: null, resolveError: null }))}
            placeholder="main"
            required
          />
        </Field>
        <Segmented
          label="Which commit"
          value={choice.source}
          onChange={(source) => onChange((c) => ({ ...c, source }))}
          options={[
            { value: 'branch', label: 'Latest commit' },
            { value: 'commit', label: 'Specific commit' },
          ]}
        />
      </div>

      {choice.source === 'branch' ? (
        choice.resolving || choice.resolvedSha || choice.resolveError ? (
        <p className="release-picker__note" aria-live="polite">
          {choice.resolving
            ? `Looking up the latest commit on ${choice.branch || 'the branch'}…`
            : choice.resolvedSha
              ? (
                <>
                  Will deploy <code>{choice.resolvedSha.slice(0, 10)}</code>, the current head of <code>{choice.branch}</code>.
                </>
              )
              : choice.resolveError
                ? `${choice.resolveError} You can still enter a specific commit.`
                : null}
        </p>
        ) : null
      ) : (
        <Field label="Commit" hint="Deployed exactly, even if the branch moves on. A short SHA is completed from the repository.">
          <Input
            id={`${idPrefix}-commit`}
            type="text"
            value={choice.commitSha}
            onChange={(e) => onChange((c) => ({ ...c, commitSha: e.target.value }))}
            placeholder="At least the first 7 characters of the commit SHA"
            className="input--mono"
            spellCheck={false}
            autoComplete="off"
          />
        </Field>
      )}
      {changes}
    </section>
  );
}

/**
 * PreviewDatabaseAction starts a database change preview of the chosen
 * Magento commit: the exact code it changes in the database, read on the
 * server against the live release without deploying anything.
 */
function PreviewDatabaseAction({
  environmentId,
  commit,
  branch,
  onCreated,
}: {
  environmentId: string;
  commit: string;
  branch: string;
  onCreated: (previewId: string) => void;
}) {
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const ready = commit.length >= 7;
  async function start() {
    setStarting(true);
    setError(null);
    try {
      const preview = await api.previewDatabaseChanges(environmentId, commit, branch);
      onCreated(preview.id);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setStarting(false);
    }
  }
  return (
    <div className="release-picker__preview">
      <button type="button" className="button button--secondary button--small" onClick={start} disabled={!ready || starting} data-testid="deploy-preview-database">
        {starting ? 'Starting preview' : 'Preview database changes'}
      </button>
      <ErrorNotice error={error} />
    </div>
  );
}
